import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import yaml from 'js-yaml';

const DEFAULT_LOCALE = 'en';
const STORAGE_KEY = 'kudos.locale';
const MESSAGE_PATHS = {
  en: '/i18n/messages.en.yml',
};

const I18nContext = createContext(null);

function getMessage(messages, key) {
  return key.split('.').reduce((value, segment) => (
    value && Object.prototype.hasOwnProperty.call(value, segment) ? value[segment] : undefined
  ), messages);
}

function interpolate(template, values = {}) {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
    const value = values[token];
    return value === undefined || value === null ? '' : String(value);
  });
}

function normalizeLocale(locale) {
  return MESSAGE_PATHS[locale] ? locale : DEFAULT_LOCALE;
}

/**
 * The BCP 47 tag to format dates and times with.
 *
 * "en" used to map to en-US, which put every date in the app in month-day order
 * and every time on a 12-hour clock -- "Fri, Sep 11" and "03:00 PM" -- for a
 * company whose people are in Romania, Norway and Sweden. en-GB keeps the
 * English month names while using the day-month order and 24-hour clock those
 * readers expect.
 */
function intlLocale(locale) {
  return locale === 'en' ? 'en-GB' : locale;
}

function createApi(locale, messages, setLocale) {
  const defaultMessages = messages || {};

  return {
    locale,
    setLocale: (nextLocale) => {
      const normalizedLocale = normalizeLocale(nextLocale);
      window.localStorage.setItem(STORAGE_KEY, normalizedLocale);
      setLocale(normalizedLocale);
    },
    t: (key, values) => {
      let message = getMessage(defaultMessages, key);
      // A message may be { one, other }: "1 lesson" and "3 lessons", chosen by values.count.
      if (message && typeof message === 'object' && typeof message.other === 'string') {
        message = Number(values?.count) === 1 && typeof message.one === 'string' ? message.one : message.other;
      }
      if (typeof message !== 'string') {
        return key;
      }

      return values ? interpolate(message, values) : message;
    },
    tm: (key, fallback = null) => getMessage(defaultMessages, key) ?? fallback,
    // In Bucharest's time whatever the browser's zone (go-live, 2026-11-01); a caller may name another.
    formatDate: (value, options) => new Intl.DateTimeFormat(intlLocale(locale), { timeZone: 'Europe/Bucharest', ...options }).format(new Date(value)),
    formatTime: (value, options) => new Intl.DateTimeFormat(intlLocale(locale), { timeZone: 'Europe/Bucharest', ...options }).format(new Date(value)),
    formatRelativeTime: (value) => {
      const diffSeconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
      if (diffSeconds < 60) return getMessage(defaultMessages, 'time.justNow') || 'just now';
      if (diffSeconds < 3600) return interpolate(getMessage(defaultMessages, 'time.minutesAgo') || '{{count}}m ago', { count: Math.floor(diffSeconds / 60) });
      if (diffSeconds < 86400) return interpolate(getMessage(defaultMessages, 'time.hoursAgo') || '{{count}}h ago', { count: Math.floor(diffSeconds / 3600) });
      return interpolate(getMessage(defaultMessages, 'time.daysAgo') || '{{count}}d ago', { count: Math.floor(diffSeconds / 86400) });
    },
  };
}

export function I18nProvider({ children }: any) {
  const [locale, setLocale] = useState(() => normalizeLocale(window.localStorage.getItem(STORAGE_KEY) || DEFAULT_LOCALE));
  const [messages, setMessages] = useState(null);

  useEffect(() => {
    let isActive = true;
    const path = MESSAGE_PATHS[locale] || MESSAGE_PATHS[DEFAULT_LOCALE];

    fetch(path)
      .then((response) => response.text())
      .then((source) => yaml.load(source))
      .then((parsed) => {
        if (isActive) {
          setMessages(parsed || {});
        }
      })
      .catch(() => {
        if (isActive) {
          setMessages({});
        }
      });

    return () => {
      isActive = false;
    };
  }, [locale]);

  const value = useMemo(() => createApi(locale, messages, setLocale), [locale, messages]);

  if (!messages) {
    return null;
  }

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }

  return context;
}
